import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaPhoneSlash } from "react-icons/fa";
import FormattedJSON from "../common/FormattedJSON";
import DialogueBubble from "../common/DialogueBubble";
import { sessionService } from "../../services/sessionService";

const EventTimelineView = ({
  initialEvents = [],
  showEvents = false,
  sessionConnectedAt = null,
}) => {
  const [events, setEvents] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [transcriptMessages, setTranscriptMessages] = useState(null);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const timelineRef = useRef(null);
  const parentRef = useRef(null);

  useEffect(() => {
    const wrapped = initialEvents.map((m) => {
      return { payload: m };
    });
    setEvents(wrapped);

    const hasTimecodedTranscriptMessages = initialEvents.some(
      (event) => event.data_type === "timecodedtranscriptmessage",
    );
    const timecodedTranscriptMessages = initialEvents.filter((msg) =>
      ["timecodedtranscriptmessage", "toolresult", "agent_action"].includes(msg.data_type),
    );
    const legacyTranscriptMessages = initialEvents.filter((msg) =>
      ["transcriptmessage", "toolresult", "agent_action"].includes(msg.data_type),
    );
    const transcriptMessages = hasTimecodedTranscriptMessages
      ? timecodedTranscriptMessages
      : legacyTranscriptMessages;
    setTranscriptMessages(transcriptMessages);
  }, []);

  useEffect(() => {
    const handleConversationUpdate = (event) => {
      setEvents((prev) => [...prev, event.detail]);
    };
    window.addEventListener("eventStreamUpdate", handleConversationUpdate);
    return () => {
      window.removeEventListener("eventStreamUpdate", handleConversationUpdate);
    };
  }, []);

  useEffect(() => {
    manageTranscriptState(events);
  }, [events]);

  useEffect(() => {
    if (sessionId && !showEvents && isSessionActive) {
      let isMounted = true;
      let intervalId;

      const setupTranscriptFetching = async () => {
        const useEvents = await checkUseEventsForTranscript();

        if (isMounted) {
          fetchTranscript(useEvents);
          intervalId = setInterval(() => fetchTranscript(useEvents), 5000);
        }
      };

      setupTranscriptFetching();

      return () => {
        isMounted = false;
        if (intervalId) clearInterval(intervalId);
      };
    }
  }, [showEvents, sessionId, isSessionActive]);

  useEffect(() => {
    if (timelineRef.current) {
      timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
    }
  }, [events]);

  const checkUseEventsForTranscript = async () => {
    const session = await sessionService.getEntry(sessionId);
    return session?.assignment_variables?.pipecat === "true";
  };

  const fetchTranscript = async (useEvents) => {
    if (useEvents) {
      const eventResponse = await sessionService.listEvents(sessionId);
      if (eventResponse && eventResponse.length > 0) {
        setTranscriptMessages(eventResponse);
      }
    } else {
      const transcriptResponse = await sessionService.getTranscript(sessionId);
      if (transcriptResponse && transcriptResponse.transcript.messages) {
        setTranscriptMessages(transcriptResponse.transcript.messages);
      }
    }
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    const time = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
      hour12: true,
    });
    const dateStr = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return { time, date: dateStr };
  };

  const formatMillisecondsToMinutes = (milliseconds) => {
    if (milliseconds === null) return "N/A";
    const absMilliseconds = Math.abs(milliseconds);

    const minutes = Math.floor(absMilliseconds / (60 * 1000));
    const seconds = Math.floor((absMilliseconds % (60 * 1000)) / 1000); // Get remaining ms, then convert to seconds
    const ms = absMilliseconds % 1000;

    const sign = milliseconds < 0 ? "-" : "";
    const formattedMinutes = String(minutes).padStart(2, "0");
    const formattedSeconds = String(seconds).padStart(2, "0");
    const formattedMilliseconds = String(ms).padStart(3, "0");

    return `${sign}${formattedMinutes}:${formattedSeconds}.${formattedMilliseconds}`;
  };

  const calculateTimeElapsed = (startTime, endTime) => {
    endTime = `${endTime.date} ${endTime.time}`;
    if (!startTime || !endTime) return formatMillisecondsToMinutes(null);

    const start = new Date(startTime);
    const end = new Date(endTime);
    const elapsedmilliSeconds = end - start;

    return formatMillisecondsToMinutes(elapsedmilliSeconds);
  };

  const formatAgentActionHeader = (data) => {
    return !!data?.blocked ? "Agent Action Blocked" : "Agent Action Invoked";
  };

  const buildAgentActionJsonData = (data) => {
    const agentName = data?.agent_name ?? "Unknown";
    const actionName = data?.action_name;
    const kwargs = data?.action_kwargs ?? {};
    const blocked = !!data?.blocked;

    let action_summary;
    if (blocked) {
      action_summary = `'${agentName}' was blocked from performing '${actionName ?? "Unknown action"}'`;
    } else if (actionName === "load_skill") {
      action_summary = `Agent loaded skill '${kwargs.skill_name ?? "Unknown"}'`;
    } else if (actionName === "finish_task") {
      action_summary = `'${agentName}' transferred to '${kwargs.next_agent ?? "unknown agent"}'`;
    } else if (actionName === "task_completed") {
      action_summary = `'${agentName}' transferred to next agent`;
    } else {
      action_summary = actionName ?? "Unknown action";
    }

    return { agent_name: agentName, action_name: actionName, action_summary };
  };

  const renderMessageBubble = (message, index) => {
    const isAgentAction = message.data_type === "agent_action";
    const isAgentActionBlocked = isAgentAction && !!message.data?.blocked;
    const isAI =
      (message.actor === "system" || message.actor === "assistant") &&
      ["outgoing-message", "audio-transcription"].includes(message.event_type);
    const isTool = message.actor === "tool";
    const isTimecodedTranscriptMessage =
      message.data_type === "timecodedtranscriptmessage";
    const { time } = formatDateTime(message.timestamp);

    const hasError = !!(
      message.data?.result?.includes(`Error occurred when calling`) ||
      message.event_type === "tool-error"
    );

    const messageText = isAgentAction
      ? formatAgentActionHeader(message.data)
      : isTool
      ? `Tool Called: ${message.data?.tool}. Results: ${message.data?.result}`
      : message.message ||
        message.data?.text ||
        message.data?.value ||
        message.data?.body ||
        message.data?.result ||
        "<Blank>";

    return (
      <DialogueBubble
        key={`${index}-${time}`}
        index={index}
        isAI={isAI}
        isTool={isTool}
        isAgentAction={isAgentAction}
        isAgentActionBlocked={isAgentActionBlocked}
        isTimecodedTranscriptMessage={isTimecodedTranscriptMessage}
        message={messageText}
        jsonData={isAgentAction ? buildAgentActionJsonData(message.data) : null}
        time={time}
        hasError={hasError}
      />
    );
  };

  const renderAssignmentClosed = (event, index, debuggingHalted = false) => {
    const { time } = formatDateTime(event.timestamp);
    return (
      <div key={`assignment-closed-${index}`} className="mx-8 px-0 mb-4">
        <div className="bg-gray-800 p-3 text-sm text-red-300 rounded-lg flex items-center space-x-4 shadow-lg transition-all duration-300 ease-in-out hover:bg-red-900 hover:text-white">
          <FaPhoneSlash className="text-2xl flex-shrink-0" />
          <div className="flex flex-col flex-grow">
            <span className="font-semibold text-right">
              {debuggingHalted ? "Debugging Halted" : "Conversation Ended"}
            </span>
            <span className="text-xs opacity-75 text-right">{time}</span>
          </div>
        </div>
      </div>
    );
  };

  const parseEventPayload = (event) => {
    const { payload } = event;
    if (payload && typeof payload === "object") return payload;
    try {
      return JSON.parse(payload);
    } catch {
      return event;
    }
  };

  const isEventMessage = (obj) => {
    return [
      "timecodedtranscriptmessage",
      "transcriptmessage",
      "toolresult",
      "agent_action",
    ].includes(obj.data_type);
  };

  const renderTranscriptMessages = () => {
    if (transcriptMessages.length === 0) {
      return (
        <div className="h-full flex items-center justify-center text-gray-500">
          <p>
            There is no transcript to view.
            <span className="block text-sm text-blue-500 mt-2">
              Consider enabling events.
            </span>
          </p>
        </div>
      );
    } else {
      return transcriptMessages.map((message, index) => {
        return renderMessageBubble(message, `transcript-${index}`);
      });
    }
  };

  const manageTranscriptState = (events) => {
    events.forEach((event) => {
      const obj = parseEventPayload(event);

      if (obj?.session_id && !sessionId) {
        setSessionId(obj.session_id);
        setIsSessionActive(true);
      }

      if (obj?.open === false) {
        setIsSessionActive(false);
      }
    });
  };

  const renderEvents = (events) => {
    let lastMessage = null;
    let lastEvent = null;
    const viewData = [];

    const hasTimecodedTranscriptMessages = events.some(
      (event) => event.data_type === "timecodedtranscriptmessage",
    );

    events.forEach((event, index) => {
      const eventObj = parseEventPayload(event);

      // If in events mode, always show the JSON first
      if (showEvents) {
        const payload = structuredClone(event.payload || eventObj);

        payload.timestamp = formatDateTime(new Date(payload.timestamp));
        payload.minutesElapsed = calculateTimeElapsed(
          sessionConnectedAt,
          payload.timestamp,
        );

        viewData.push(
          <React.Fragment key={`advanced-${index}`}>
            <FormattedJSON json={JSON.stringify(payload, null, 2)} />
          </React.Fragment>,
        );
      }

      // Then handle the specific message types
      if (isEventMessage(eventObj)) {
        // Render a message bubble
        const messageText =
          eventObj.data?.text ||
          eventObj.data?.value ||
          eventObj.data?.body ||
          eventObj.data?.result ||
          "<Blank>";

        // Still check for duplicates; or if events have timecoded transcript messages, don't show non-timecoded transcript messages
        if (
          (hasTimecodedTranscriptMessages &&
            eventObj.data_type !== "transcriptmessage") ||
          !lastMessage ||
          lastMessage.actor !== eventObj.actor ||
          lastMessage.text !== messageText
        ) {
          lastMessage = { actor: eventObj.actor, text: messageText };
          lastEvent = null;
          viewData.push(renderMessageBubble(eventObj, `bubble-${index}`));
        }
      } else if (
        event.event === "assignment_closed" ||
        event.event === "debugging_halted"
      ) {
        // Render assignment closed
        if (lastEvent !== event.event) {
          lastEvent = event.event;
          lastMessage = null;
          viewData.push(
            renderAssignmentClosed(
              event,
              `closed-${index}`,
              event.event === "debugging_halted",
            ),
          );
        }
      }
      // Note: We no longer have an "else if (showEvents)" here because we handle that at the beginning
    });

    return viewData;
  };

  return (
    <div className="h-full flex flex-col text-white relative" ref={parentRef}>
      <div
        ref={timelineRef}
        className="flex-grow overflow-y-auto p-4 dark-slim-scrollbar"
      >
        {!showEvents &&
          transcriptMessages &&
          transcriptMessages.length > 0 &&
          renderTranscriptMessages()}
        {showEvents && renderEvents(events)}
      </div>
    </div>
  );
};

export default React.memo(EventTimelineView);
